import { Comment } from '@api/twin/CommentApi';
import { datadogRum } from '@datadog/browser-rum';
import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';

interface AnomalyCommentContextProps {
  comment: Comment;
  isResponseListOpened: boolean;
  toggleResponseList: () => void;
  isAnswering: boolean;
  toggleAnswering: () => void;
}

interface AnomalyCommentProviderProps {
  comment: Comment;
  children: ReactNode;
}

const AnomalyCommentContext = createContext<AnomalyCommentContextProps | null>(null);

export const AnomalyCommentProvider = ({ comment, children }: AnomalyCommentProviderProps) => {
  const [isResponseListOpened, setIsResponseListOpened] = useState(false);
  const [isAnswering, setIsAnswering] = useState(false);

  const toggleResponseList = useCallback(() => {
    datadogRum.addAction('tvp.anomaly.comment.response_list', {
      isOpen: isResponseListOpened,
    });
    setIsResponseListOpened((prev) => !prev);
  }, [isResponseListOpened]);

  const toggleAnswering = useCallback(() => {
    datadogRum.addAction('tvp.anomaly.comment.open_answer', {
      isOpen: isAnswering,
    });
    setIsAnswering((prev) => !prev);
  }, [isAnswering]);

  const context = useMemo(
    () => ({
      comment,
      isResponseListOpened,
      toggleResponseList,
      isAnswering,
      toggleAnswering,
    }),
    [comment, isResponseListOpened, isAnswering, toggleAnswering, toggleResponseList],
  );

  return (
    <AnomalyCommentContext.Provider value={context}>{children}</AnomalyCommentContext.Provider>
  );
};

export const useAnomalyCommentContext = () => {
  const context = useContext(AnomalyCommentContext);
  if (!context) {
    throw new Error('useAnomalyCommentContext must be used within an AnomalyCommentProvider');
  }

  return context;
};
